/* eslint-disable react/no-danger */
import { IAggregationProduct } from "api/interfaces/get-page-details/common/category-page.type";
import { IBreadCrumbItem } from "api/interfaces/get-page-details/common/nested-pagecontent.type";
import { IPageContent } from "api/interfaces/static-content.type";
import FAQMetaHead from "component/meta-header/FAQMetaHead";
import LocalBusinessMeta from "component/meta-header/LocalBusinessMeta";
import OrganizationMetaHeader from "component/meta-header/OrganizationMetaHeader";
import SiteLinkMeta from "component/meta-header/SiteLinkMeta";
import Head from "next/head";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import { getProductHeading } from "util/product/get-product-heading";
import { capitalize } from "../../util/lodash/capitalize";
import { getThumbnailSrcV2 } from "../../util/product/getThumbmailSrc";
import withErrorHandler from "../design-system/withErrorHandler";
import BreadcrumbsMetaHead from "../meta-header/BreadcrumbsMetaHead";

interface IProps{
  staticContent: IPageContent;
  tier3: string;
  tier2: string;
  tier1: string;
  tier0: string;
  make: string;
  model: string;
  year: string;
  totalHits: number;
  products: IAggregationProduct[];
  query: any;
  pageSize: number;
  breadCrumbsList: IBreadCrumbItem[];
}

function CategoryMetaHead(props: IProps) {
  const {
    query, totalHits, products,
    tier3, tier2, tier1, tier0,
    make, model, year,
    staticContent, pageSize, breadCrumbsList, locale,
  } = props;
  const { pathname } = query;
  const { metaData, links } = staticContent;
  let {
    keywords, description, title,
  } = metaData || {};
  const { keywordsV2 } = metaData || {};

  const { contentIds } = links || {};
  const { bannerImageUrl = "" } = staticContent.pageDetails || {};
  let { pageNumber = 1 } = query;
  const totalPages = Math.ceil(totalHits / pageSize);

  if (!description && !title) {
    const tierString = capitalize(tier3 || tier2 || tier1 || tier0);
    if (make || model || year) {
      const vehicleString = [year, make, model].filter(Boolean).map((_) => capitalize(_)).join(" ");
      description = `Largest selection of high-quality ${tierString} to fit your ${vehicleString} here. Get All ${vehicleString} auto parts at PartsAvatar.ca`;
      title = `${vehicleString} ${tierString}`;
    } else {
      description = `Largest selection of high-quality ${tierString} for your Car, SUV or Truck. Buy auto parts that fit your vehicle with confidence and free shipping.
      `;
      title = `PartsAvatar.ca: ${tierString}`;
    }
  }

  if (!keywords) {
    keywords = make ? `${year} ${make} ${model} ${tier3 || tier2 || tier1 || tier0}` : `${tier3 || tier2 || tier1 || tier0}`;
  } else if (Array.isArray(keywords)) {
    keywords = keywords.join(", ");
  }

  title = title.trim();
  const meta = {
    title,
    image: bannerImageUrl,
    description,
    meta: {
      robots: "all",
    },
    keywords: keywordsV2 || keywords,
  };

  const productsArray = [];
  const itemListElement = products.map((product: any, position: any) => {
    const {
      canonicalUrl, manufacturer, description: pDesc,
      manufacturerSmallImageUrl, partNumber, upc, sku, price,
      outOfStock, longDescription,
    } = product;
    const thumbnail = getThumbnailSrcV2(product);

    const productUrl = `https://partsavatar.ca${canonicalUrl}`;

    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 5);

    const productMicroData = {
      "@context": "http://schema.org/",
      "@type": "Product",

      name: getProductHeading(product),
      description: longDescription,
      sku,
      gtin13: upc,
      mpn: partNumber,
      url: productUrl,
      image: thumbnail,
      brand: {
        "@type": "http://schema.org/Brand",
        name: manufacturer,
        logo: getRelativeImagePath(manufacturerSmallImageUrl),
      },
      offers: {
        "@type": "Offer",
        priceCurrency: "CAD",
        priceValidUntil: `${nextMonth.getFullYear()}-${nextMonth.getMonth() + 1}-05`,
        // Todo: Check where this is used
        price: (price / 100).toFixed(2),
        availability: `http://schema.org/${outOfStock ? "OutOfStock" : "InStock"}`,
        url: productUrl,
        itemCondition: "https://schema.org/NewCondition",
      },
    };

    productsArray.push(productMicroData);

    return {
      "@type": "ListItem",
      position,
      url: `https://partsavatar.ca${canonicalUrl}`,
      name: pDesc,
      description: longDescription,
      image: thumbnail,
    };
  });

  const microdata = {
    "@context": "http://schema.org/",
    "@type": "CollectionPage",
    mainEntity: {
      "@type": "ItemList",
      itemListElement,
      numberOfItems: totalHits,
    },
  };

  pageNumber = parseInt(pageNumber, 10);
  const url = `https://partsavatar.ca/${pathname}`;
  const canonicalUrl = pageNumber > 1 ? `https://partsavatar.ca/${pathname}?pageNumber=${pageNumber}` : `https://partsavatar.ca/${pathname}`;
  const frCanonicalUrl = pageNumber > 1 ? `https://partsavatar.ca/fr/${pathname}?pageNumber=${pageNumber}` : `https://partsavatar.ca/fr/${pathname}`;

  return (
    <>

      <Head>

        <title>
          {meta.title}
        </title>

        <meta name="facebook-domain-verification" content="82cgyip0cz3a7igt87rjow5o8bf9th" />
        <meta name="description" content={meta.description} />

        <meta name="keywords" content={meta.keywords} />

        <meta name="robots" content={pageNumber > 5 ? "noindex" : meta.meta.robots} />

        <link rel="canonical" href={locale === "fr" ? frCanonicalUrl : canonicalUrl} />
        <link rel="alternate" hrefLang="en" href={canonicalUrl} />
        <link rel="alternate" hrefLang="fr" href={frCanonicalUrl} />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

        {pageNumber > 2 && pageNumber <= totalPages && (
          <link
            rel="prev"
            href={`https://partsavatar.ca/${pathname}?pageNumber=${pageNumber - 1}`}
          />
        )}
        {pageNumber === 2 && pageNumber <= totalPages && (
          <link
            rel="prev"
            href={`https://partsavatar.ca/${pathname}`}
          />
        )}
        {pageNumber < totalPages && (
          <link
            rel="next"
            href={`https://partsavatar.ca/${pathname}?pageNumber=${pageNumber + 1}`}
          />
        )}

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(microdata) }}
        />

        <meta property="og:locale" content="en_CA" />
        <meta property="og:locale:alternate" content="en_CA" />
        <meta property="og:locale:alternate" content="fr_CA" />

        <meta property="og:title" content={meta.title} />

        <meta property="og:description" content={meta.description} />

        <meta property="og:url" content={url} />

        <meta property="og:site_name" content="PartsAvatar.ca" />

        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />

        <meta name="twitter:title" content={meta.title} />

        <meta name="twitter:description" content={meta.description} />

        <meta name="twitter:image" content={meta.image} />
        <meta property="og:type" content="product.group" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "http://schema.org",
              "@type": "WebPage",
              primaryImageOfPage: {
                "@type": "ImageObject",
                url: bannerImageUrl,
              },
            }),
          }}
        />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productsArray) }} />
      </Head>
      <BreadcrumbsMetaHead {...{ breadCrumbList: breadCrumbsList }} />
      <SiteLinkMeta />
      <OrganizationMetaHeader />
      <LocalBusinessMeta />
      {contentIds && (
        <FAQMetaHead contentIds={contentIds} />
      )}

    </>
  );
}

export default withErrorHandler(CategoryMetaHead);
